import { useMemo } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  AppShell,
  Group,
  Text,
  Button,
  Menu,
  Avatar,
  Indicator,
  ActionIcon,
  Burger,
  Drawer,
  Stack,
  NavLink,
  Divider,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { SkipLink } from '@/components/SkipLink';
import { RouteAnnouncer } from '@/components/RouteAnnouncer';
import {
  IconShoppingCart,
  IconUser,
  IconSettings,
  IconLogout,
  IconChevronDown,
  IconLeaf,
  IconHome,
  IconBuildingStore,
  IconClipboardList,
  IconFileInvoice,
  IconCash,
  IconCreditCard,
  IconFileText,
  IconReceiptRefund,
  IconMapPin,
  IconAlertTriangle,
  IconCurrencyDollar,
  IconCalendarTime,
  IconFileCheck,
  IconPlug,
  IconRecycle,
  IconTrendingUp,
  IconRefresh,
  IconRobot,
  IconHeadset,
  IconFiles,
  IconUsersGroup,
  IconChartBar,
  IconFlag,
  IconApple,
  IconSchool,
  IconAlertCircle,
  IconRepeat,
  IconFileDescription,
  IconArrowBack,
  IconFileInvoice as IconQuote,
  IconShieldCheck,
  IconDeviceMobile,
  IconTemperature,
  IconInbox,
  IconMicrophone,
  IconPhone,
  IconWorld,
  IconMoodSmile,
  IconCalendar,
  IconNews,
  IconReplace,
  IconToolsKitchen2,
  IconNotes,
  IconCalendarEvent,
  IconArrowsExchange,
} from '@tabler/icons-react';
import { useAuthStore } from '@/store/auth';
import { useCartStore } from '@/store/cart';
import { canSee } from '@/config/customerModules';
import type { NavSection } from '@/config/customerModules';
import CartDrawer from '@/components/CartDrawer';
import NotificationBell from '@/components/NotificationBell';
import NextTaskSuggestion from '@/components/NextTaskSuggestion';
import { useTaskAnticipation } from '@/hooks/useTaskAnticipation';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface NavItem {
  label: string;
  path: string;
  icon: React.ComponentType<any>;
  exact?: boolean;
  section?: NavSection;
}

const NAV_LINKS: NavItem[] = [
  { label: 'Home', path: '/dashboard', icon: IconHome, exact: true },
  { label: 'Shop', path: '/shop', icon: IconBuildingStore },
];

const ORDERS_LINKS: NavItem[] = [
  { label: 'My Orders', path: '/orders', icon: IconClipboardList },
  { label: 'Quick Reorder', path: '/orders/quick-reorder', icon: IconRefresh },
  { label: 'Smart Reorder', path: '/orders/smart-reorder', icon: IconRobot, section: 'smart_reorder' },
  { label: 'Recurring', path: '/orders/recurring', icon: IconRefresh },
  { label: 'Templates', path: '/orders/templates', icon: IconClipboardList },
  { label: 'Par Levels', path: '/orders/par-levels', icon: IconChartBar, section: 'par_levels' },
  { label: 'Sub Preferences', path: '/orders/substitution-rules', icon: IconRecycle },
  { label: 'Quotes', path: '/quotes', icon: IconQuote },
  { label: 'Request Quote', path: '/quotes/rfq', icon: IconQuote },
  { label: 'Returns', path: '/returns', icon: IconArrowBack },
  { label: 'Deliveries', path: '/delivery/calendar', icon: IconCalendarTime },
  { label: 'Invoices', path: '/invoices', icon: IconFileInvoice },
  // Internal ops — hidden from customers via section gate
  { label: 'Order Intake', path: '/orders/intake', icon: IconInbox, section: 'order_intake' },
  { label: 'Voicemail Orders', path: '/orders/voicemail', icon: IconMicrophone, section: 'order_intake' },
  { label: 'Voice Calls', path: '/orders/voice-calls', icon: IconPhone, section: 'order_intake' },
  { label: 'E-Commerce', path: '/orders/ecommerce', icon: IconWorld, section: 'order_intake' },
  { label: 'Smart Subs', path: '/orders/smart-substitutions', icon: IconReplace, section: 'order_intake' },
  { label: 'Substitutions', path: '/orders/substitutions', icon: IconRecycle, section: 'order_intake' },
];

const HELP_LINKS: NavItem[] = [
  { label: 'Claims', path: '/claims', icon: IconAlertTriangle },
  { label: 'Warranty', path: '/warranty', icon: IconShieldCheck },
  { label: 'Quality Feedback', path: '/delivery/feedback', icon: IconFileCheck },
  { label: 'Support', path: '/support', icon: IconHeadset },
  { label: 'Documents', path: '/documents', icon: IconFiles },
  { label: 'Seasonal Calendar', path: '/shop/seasonal', icon: IconLeaf },
  { label: 'Sensors', path: '/sensors', icon: IconTemperature },
  { label: 'News', path: '/social', icon: IconNews },
];

const BILLING_LINKS: NavItem[] = [
  { label: 'Payment History', path: '/billing/payments', icon: IconCash },
  { label: 'Make Payment', path: '/billing/pay', icon: IconCreditCard },
  { label: 'Statement', path: '/billing/statement', icon: IconFileText },
  { label: 'Credit Memos', path: '/billing/credits', icon: IconReceiptRefund },
  { label: 'Dispute Center', path: '/billing/disputes', icon: IconAlertCircle },
  { label: 'AutoPay', path: '/billing/autopay', icon: IconRepeat },
];

const PRICING_LINKS: NavItem[] = [
  { label: 'My Prices', path: '/pricing/my-prices', icon: IconCurrencyDollar },
  { label: 'Price Match', path: '/pricing/price-match', icon: IconCurrencyDollar },
  { label: 'Future Prices', path: '/pricing/future-prices', icon: IconTrendingUp, section: 'future_prices' },
  { label: 'Price Watch', path: '/pricing/watch', icon: IconTrendingUp, section: 'price_watch' },
];

const SCHOOL_COMPLIANCE_LINKS: NavItem[] = [
  { label: 'Buy America', path: '/compliance/buy-america', icon: IconFlag },
  { label: 'FFVP', path: '/compliance/ffvp', icon: IconApple },
  { label: 'Sales Analysis', path: '/compliance/sales-analysis', icon: IconChartBar },
];

const MENU_PLANNER_LINKS: NavItem[] = [
  { label: 'Menu Hub', path: '/menu-planner', icon: IconToolsKitchen2 },
  { label: 'My Menus', path: '/menu-planner/menus', icon: IconClipboardList },
  { label: 'Recipe to Cart', path: '/menu-planner/recipe', icon: IconNotes },
  { label: 'Event Planner', path: '/menu-planner/events', icon: IconCalendarEvent },
  { label: 'Seasonal Picks', path: '/menu-planner/seasonal', icon: IconLeaf },
  { label: 'Substitutes', path: '/menu-planner/substitutes', icon: IconArrowsExchange },
];

export default function PortalLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const cartCount = useCartStore((s) => s.itemCount);
  const [mobileOpen, { toggle: toggleMobile, close: closeMobile }] = useDisclosure(false);
  const [cartOpen, { open: openCart, close: closeCart }] = useDisclosure(false);
  const { suggestions, mode } = useTaskAnticipation();

  const customerType = user?.customer?.customer_type ?? 'wholesale';

  const filterLinks = useMemo(
    () => (links: NavItem[]) =>
      links.filter((link) => !link.section || canSee(customerType, link.section)),
    [customerType],
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const displayName = user?.display_name || user?.customer?.name || user?.username || 'Customer';
  const initials = displayName
    .split(' ')
    .map((w: string) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const isLinkActive = (link: NavItem) =>
    link.exact ? location.pathname === link.path : location.pathname.startsWith(link.path);

  const isOrdersActive = location.pathname.startsWith('/orders') || location.pathname.startsWith('/invoices') || location.pathname.startsWith('/delivery/calendar') || location.pathname.startsWith('/quotes') || location.pathname.startsWith('/returns');
  const isBillingActive = location.pathname.startsWith('/billing');
  const isPricingActive = location.pathname.startsWith('/pricing');
  const isHelpActive = location.pathname.startsWith('/claims') || location.pathname.startsWith('/support') || location.pathname.startsWith('/documents') || location.pathname === '/delivery/feedback' || location.pathname.startsWith('/warranty') || location.pathname.startsWith('/sensors') || location.pathname.startsWith('/social');
  const isComplianceActive = location.pathname.startsWith('/compliance/buy-america') || location.pathname.startsWith('/compliance/ffvp') || location.pathname.startsWith('/compliance/sales-analysis');
  const isMenuPlannerActive = location.pathname.startsWith('/menu-planner');

  const showCompliance = canSee(customerType, 'school_compliance');

  const filteredOrders = filterLinks(ORDERS_LINKS);
  const filteredBilling = filterLinks(BILLING_LINKS);
  const filteredPricing = filterLinks(PRICING_LINKS);

  return (
    <AppShell header={{ height: 110 }} padding="md">
      <SkipLink />
      <RouteAnnouncer pathname={location.pathname} appName="Standard Produce Portal" />
      <AppShell.Header style={{ boxShadow: 'var(--mantine-shadow-sm)', border: 'none' }}>
        <Group h="100%" px="lg" justify="space-between">
          {/* Left: Brand + Mobile burger */}
          <Group gap="sm">
            <Burger
              opened={mobileOpen}
              onClick={toggleMobile}
              hiddenFrom="sm"
              size="sm"
              aria-label="Toggle navigation menu"
            />
            <Group
              gap={8}
              renderRoot={(props) => <Link to="/dashboard" {...props} style={{ ...props.style, textDecoration: 'none' }} />}
            >
              <img src="/logo.png" alt="Standard Produce Co." style={{ height: 100 }} />
            </Group>
          </Group>

          {/* Center: Nav links (desktop) */}
          <Group gap={4} visibleFrom="sm" style={{ flexWrap: 'nowrap' }} role="navigation" aria-label="Main navigation">
            {NAV_LINKS.map((link) => (
              <Button
                key={link.path}
                component={Link}
                to={link.path}
                variant={isLinkActive(link) ? 'light' : 'subtle'}
                size="sm"
                radius="xl"
                leftSection={<link.icon size={16} />}
                aria-current={isLinkActive(link) ? 'page' : undefined}
                styles={{
                  root: {
                    fontWeight: isLinkActive(link) ? 600 : 500,
                  },
                }}
              >
                {link.label}
              </Button>
            ))}

            {/* Menu Planner dropdown */}
            <Menu shadow="md" width={220} position="bottom-start" radius="md">
              <Menu.Target>
                <Button variant={isMenuPlannerActive ? 'light' : 'subtle'} size="sm" radius="xl" leftSection={<IconToolsKitchen2 size={16} />} rightSection={<IconChevronDown size={14} />} styles={{ root: { fontWeight: isMenuPlannerActive ? 600 : 500 } }}>
                  Menu Planner
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {MENU_PLANNER_LINKS.map((link) => (
                  <Menu.Item key={link.path} leftSection={<link.icon size={16} />} onClick={() => navigate(link.path)} style={{ fontWeight: location.pathname === link.path ? 600 : undefined, backgroundColor: location.pathname === link.path ? 'var(--mantine-color-brand-0)' : undefined }}>
                    {link.label}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>

            {/* Orders dropdown */}
            <Menu shadow="md" width={220} position="bottom-start" radius="md">
              <Menu.Target>
                <Button variant={isOrdersActive ? 'light' : 'subtle'} size="sm" radius="xl" leftSection={<IconClipboardList size={16} />} rightSection={<IconChevronDown size={14} />} styles={{ root: { fontWeight: isOrdersActive ? 600 : 500 } }}>
                  Orders
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {filteredOrders.map((link) => (
                  <Menu.Item key={link.path} leftSection={<link.icon size={16} />} onClick={() => navigate(link.path)} style={{ fontWeight: location.pathname === link.path ? 600 : undefined, backgroundColor: location.pathname === link.path ? 'var(--mantine-color-brand-0)' : undefined }}>
                    {link.label}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>

            {/* Billing dropdown */}
            <Menu shadow="md" width={200} position="bottom-start" radius="md">
              <Menu.Target>
                <Button variant={isBillingActive ? 'light' : 'subtle'} size="sm" radius="xl" leftSection={<IconCash size={16} />} rightSection={<IconChevronDown size={14} />} styles={{ root: { fontWeight: isBillingActive ? 600 : 500 } }}>
                  Billing
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {filteredBilling.map((link) => (
                  <Menu.Item key={link.path} leftSection={<link.icon size={16} />} onClick={() => navigate(link.path)} style={{ fontWeight: location.pathname === link.path ? 600 : undefined, backgroundColor: location.pathname === link.path ? 'var(--mantine-color-brand-0)' : undefined }}>
                    {link.label}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>

            {/* Pricing dropdown */}
            <Menu shadow="md" width={200} position="bottom-start" radius="md">
              <Menu.Target>
                <Button variant={isPricingActive ? 'light' : 'subtle'} size="sm" radius="xl" leftSection={<IconCurrencyDollar size={16} />} rightSection={<IconChevronDown size={14} />} styles={{ root: { fontWeight: isPricingActive ? 600 : 500 } }}>
                  Pricing
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {filteredPricing.map((link) => (
                  <Menu.Item key={link.path} leftSection={<link.icon size={16} />} onClick={() => navigate(link.path)} style={{ fontWeight: location.pathname === link.path ? 600 : undefined, backgroundColor: location.pathname === link.path ? 'var(--mantine-color-brand-0)' : undefined }}>
                    {link.label}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>

            {/* School Compliance dropdown — only for school customers */}
            {showCompliance && (
              <Menu shadow="md" width={220} position="bottom-start" radius="md">
                <Menu.Target>
                  <Button variant={isComplianceActive ? 'light' : 'subtle'} size="sm" radius="xl" leftSection={<IconSchool size={16} />} rightSection={<IconChevronDown size={14} />} styles={{ root: { fontWeight: isComplianceActive ? 600 : 500 } }}>
                    Compliance
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  {SCHOOL_COMPLIANCE_LINKS.map((link) => (
                    <Menu.Item key={link.path} leftSection={<link.icon size={16} />} onClick={() => navigate(link.path)} style={{ fontWeight: location.pathname === link.path ? 600 : undefined, backgroundColor: location.pathname === link.path ? 'var(--mantine-color-brand-0)' : undefined }}>
                      {link.label}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            )}

            {/* Help dropdown */}
            <Menu shadow="md" width={220} position="bottom-start" radius="md">
              <Menu.Target>
                <Button variant={isHelpActive ? 'light' : 'subtle'} size="sm" radius="xl" leftSection={<IconHeadset size={16} />} rightSection={<IconChevronDown size={14} />} styles={{ root: { fontWeight: isHelpActive ? 600 : 500 } }}>
                  Help
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {HELP_LINKS.map((link) => (
                  <Menu.Item key={link.path} leftSection={<link.icon size={16} />} onClick={() => navigate(link.path)} style={{ fontWeight: location.pathname === link.path ? 600 : undefined, backgroundColor: location.pathname === link.path ? 'var(--mantine-color-brand-0)' : undefined }}>
                    {link.label}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          </Group>

          {/* Right: Cart, Notifications, User menu */}
          <Group gap="sm">
            <Indicator label={cartCount} size={18} disabled={cartCount === 0}>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="lg"
                radius="xl"
                onClick={openCart}
                aria-label={`Shopping cart${cartCount > 0 ? `, ${cartCount} items` : ''}`}
              >
                <IconShoppingCart size={22} />
              </ActionIcon>
            </Indicator>

            <NotificationBell />

            <Menu shadow="lg" width={220} position="bottom-end" radius="md">
              <Menu.Target>
                <Button
                  variant="subtle"
                  color="gray"
                  size="sm"
                  px="xs"
                  radius="xl"
                  aria-label={`User menu for ${displayName}`}
                >
                  <Group gap={8}>
                    <Avatar radius="xl" size="sm" color="brand" variant="filled">
                      {initials}
                    </Avatar>
                    <Text size="sm" fw={500} visibleFrom="sm">
                      {displayName}
                    </Text>
                    <IconChevronDown size={14} />
                  </Group>
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconUser size={16} />}
                  onClick={() => navigate('/account')}
                >
                  Profile
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconMapPin size={16} />}
                  onClick={() => navigate('/account/addresses')}
                >
                  Addresses
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconSettings size={16} />}
                  onClick={() => navigate('/account/preferences')}
                >
                  Preferences
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconCreditCard size={16} />}
                  onClick={() => navigate('/account/credit')}
                >
                  Credit Status
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconFileCheck size={16} />}
                  onClick={() => navigate('/account/tax-certificates')}
                >
                  Tax Certificates
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconUsersGroup size={16} />}
                  onClick={() => navigate('/account/team')}
                >
                  Account Team
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconPlug size={16} />}
                  onClick={() => navigate('/account/integrations')}
                >
                  Integrations
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconDeviceMobile size={16} />}
                  onClick={() => navigate('/account/sms-preferences')}
                >
                  SMS Preferences
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconMoodSmile size={16} />}
                  onClick={() => navigate('/account/satisfaction')}
                >
                  My Satisfaction
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconCalendar size={16} />}
                  onClick={() => navigate('/account/activities')}
                >
                  My Activities
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconFileDescription size={16} />}
                  onClick={() => navigate('/account/credit-application')}
                >
                  Apply for Credit
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconFileInvoice size={16} />}
                  onClick={() => navigate('/account/net-terms-request')}
                >
                  Change Net Terms
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconCalendarTime size={16} />}
                  onClick={() => navigate('/account/statement-cycle')}
                >
                  Statement Cycle
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconLogout size={16} />}
                  onClick={handleLogout}
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      {/* Mobile nav drawer */}
      <Drawer
        opened={mobileOpen}
        onClose={closeMobile}
        size="xs"
        aria-label="Mobile navigation"
        title={
          <Group gap={8}>
            <img src="/logo.png" alt="Standard Produce Co." style={{ height: 80 }} />
          </Group>
        }
        hiddenFrom="sm"
      >
        <Stack gap={4} mt="sm" role="navigation" aria-label="Mobile navigation links">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.path}
              label={link.label}
              leftSection={<link.icon size={20} />}
              active={isLinkActive(link)}
              aria-current={isLinkActive(link) ? 'page' : undefined}
              onClick={() => {
                navigate(link.path);
                closeMobile();
              }}
              variant="light"
            />
          ))}
          <Divider my="sm" />
          <Text size="xs" c="dimmed" fw={700} tt="uppercase" px="sm" mb={4}>
            Orders
          </Text>
          {filteredOrders.map((link) => (
            <NavLink
              key={link.path}
              label={link.label}
              leftSection={<link.icon size={20} />}
              active={location.pathname === link.path}
              onClick={() => {
                navigate(link.path);
                closeMobile();
              }}
              variant="light"
            />
          ))}
          <Divider my="sm" />
          <Text size="xs" c="dimmed" fw={700} tt="uppercase" px="sm" mb={4}>
            Billing
          </Text>
          {filteredBilling.map((link) => (
            <NavLink
              key={link.path}
              label={link.label}
              leftSection={<link.icon size={20} />}
              active={location.pathname === link.path}
              onClick={() => {
                navigate(link.path);
                closeMobile();
              }}
              variant="light"
            />
          ))}
          <Divider my="sm" />
          <Text size="xs" c="dimmed" fw={700} tt="uppercase" px="sm" mb={4}>
            Pricing
          </Text>
          {filteredPricing.map((link) => (
            <NavLink
              key={link.path}
              label={link.label}
              leftSection={<link.icon size={20} />}
              active={location.pathname === link.path}
              onClick={() => {
                navigate(link.path);
                closeMobile();
              }}
              variant="light"
            />
          ))}
          {showCompliance && (
            <>
              <Divider my="sm" />
              <Text size="xs" c="dimmed" fw={700} tt="uppercase" px="sm" mb={4}>
                Compliance
              </Text>
              {SCHOOL_COMPLIANCE_LINKS.map((link) => (
                <NavLink
                  key={link.path}
                  label={link.label}
                  leftSection={<link.icon size={20} />}
                  active={location.pathname === link.path}
                  onClick={() => {
                    navigate(link.path);
                    closeMobile();
                  }}
                  variant="light"
                />
              ))}
            </>
          )}
          <Divider my="sm" />
          <Text size="xs" c="dimmed" fw={700} tt="uppercase" px="sm" mb={4}>
            Menu Planner
          </Text>
          {MENU_PLANNER_LINKS.map((link) => (
            <NavLink
              key={link.path}
              label={link.label}
              leftSection={<link.icon size={20} />}
              active={location.pathname === link.path}
              onClick={() => {
                navigate(link.path);
                closeMobile();
              }}
              variant="light"
            />
          ))}
          <Divider my="sm" />
          <Text size="xs" c="dimmed" fw={700} tt="uppercase" px="sm" mb={4}>
            Help & Resources
          </Text>
          {HELP_LINKS.map((link) => (
            <NavLink
              key={link.path}
              label={link.label}
              leftSection={<link.icon size={20} />}
              active={location.pathname === link.path}
              onClick={() => {
                navigate(link.path);
                closeMobile();
              }}
              variant="light"
            />
          ))}
          <Divider my="sm" />
          <Text size="xs" c="dimmed" fw={700} tt="uppercase" px="sm" mb={4}>
            Account
          </Text>
          <NavLink
            label="Profile"
            leftSection={<IconUser size={20} />}
            active={location.pathname === '/account'}
            onClick={() => {
              navigate('/account');
              closeMobile();
            }}
            variant="light"
          />
          <NavLink
            label="Addresses"
            leftSection={<IconMapPin size={20} />}
            active={location.pathname === '/account/addresses'}
            onClick={() => {
              navigate('/account/addresses');
              closeMobile();
            }}
            variant="light"
          />
          <NavLink
            label="Preferences"
            leftSection={<IconSettings size={20} />}
            active={location.pathname === '/account/preferences'}
            onClick={() => {
              navigate('/account/preferences');
              closeMobile();
            }}
            variant="light"
          />
          <NavLink
            label="Credit Status"
            leftSection={<IconCreditCard size={20} />}
            active={location.pathname === '/account/credit'}
            onClick={() => {
              navigate('/account/credit');
              closeMobile();
            }}
            variant="light"
          />
          <NavLink
            label="Tax Certificates"
            leftSection={<IconFileCheck size={20} />}
            active={location.pathname === '/account/tax-certificates'}
            onClick={() => {
              navigate('/account/tax-certificates');
              closeMobile();
            }}
            variant="light"
          />
          <NavLink
            label="Account Team"
            leftSection={<IconUsersGroup size={20} />}
            active={location.pathname === '/account/team'}
            onClick={() => {
              navigate('/account/team');
              closeMobile();
            }}
            variant="light"
          />
          <NavLink
            label="SMS Preferences"
            leftSection={<IconDeviceMobile size={20} />}
            active={location.pathname === '/account/sms-preferences'}
            onClick={() => {
              navigate('/account/sms-preferences');
              closeMobile();
            }}
            variant="light"
          />
          <NavLink
            label="My Satisfaction"
            leftSection={<IconMoodSmile size={20} />}
            active={location.pathname === '/account/satisfaction'}
            onClick={() => {
              navigate('/account/satisfaction');
              closeMobile();
            }}
            variant="light"
          />
          <NavLink
            label="My Activities"
            leftSection={<IconCalendar size={20} />}
            active={location.pathname === '/account/activities'}
            onClick={() => {
              navigate('/account/activities');
              closeMobile();
            }}
            variant="light"
          />
          <NavLink
            label="Apply for Credit"
            leftSection={<IconFileDescription size={20} />}
            active={location.pathname === '/account/credit-application'}
            onClick={() => {
              navigate('/account/credit-application');
              closeMobile();
            }}
            variant="light"
          />
          <NavLink
            label="Change Net Terms"
            leftSection={<IconFileInvoice size={20} />}
            active={location.pathname === '/account/net-terms-request'}
            onClick={() => {
              navigate('/account/net-terms-request');
              closeMobile();
            }}
            variant="light"
          />
          <NavLink
            label="Statement Cycle"
            leftSection={<IconCalendarTime size={20} />}
            active={location.pathname === '/account/statement-cycle'}
            onClick={() => {
              navigate('/account/statement-cycle');
              closeMobile();
            }}
            variant="light"
          />
        </Stack>
      </Drawer>

      {/* Cart drawer */}
      <CartDrawer opened={cartOpen} onClose={closeCart} />

      <AppShell.Main id="main-content" role="main" tabIndex={-1}>
        <NextTaskSuggestion suggestions={suggestions} mode={mode} />
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
